/**
 * Phase 20 / Task 20.7 — §80 step 4: the import running, its result, and undo.
 *
 * `POST …/commit` answers 202 and the rows are written in the background
 * (DEC-282). This screen polls `GET …/commit-status` every `POLL_MS` while the
 * commit is QUEUED or RUNNING and stops the moment it is DONE or FAILED. The
 * progress is real: `done` is a count of rows with an outcome, which the engine
 * stamps in each row's own committed transaction, so it is exact and survives a
 * resume.
 *
 * A dry run's progress is indeterminate (`done` is null — its outcomes are
 * uncommitted until they are discarded), and it says "previewing" rather than
 * showing a bar stuck at 0%.
 *
 * The result workbook link is signed on click from `GET …/result-file`, never
 * per poll.
 *
 * ── Undo ───────────────────────────────────────────────────────────────────
 * Removes only the records this import CREATED, and only while nothing refers
 * to them. A refusal is a 200 with `complete: false`; its blocker messages name
 * the table and column in the way and are shown as they are. A refusal removes
 * nothing — the server checks every record before deleting any — so `deleted`
 * is 0 on one and the screen says "Nothing was removed." The count is still
 * shown as reported rather than assumed: the screen says what the server says
 * happened, not what a refusal is supposed to mean.
 */

import * as React from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useApiOpts } from '@lodex-pro/web-shared';

import { getCommitStatus, getResultFile, ImportApiError, undoImport } from './api';
import { familyLabel } from './mapping-state';
import {
  POLL_MS,
  blockerLines,
  canUndo,
  entityLabel,
  entityPhase,
  isWaiting,
  percentDone,
  summaryOf,
} from './run-state';
import type { CommitMode, CommitStatus, EntityProgress, UndoReport } from './types';
import { ActionButton, Notice, Panel, muted } from './ui';

function count(n: number, one: string, many: string = `${one}s`): string {
  return `${n.toString()} ${n === 1 ? one : many}`;
}

function Bar(props: {
  readonly percent: number | null;
  readonly label: string;
  readonly height?: number;
}): React.JSX.Element {
  const height = props.height ?? 8;
  return (
    <div
      role="progressbar"
      aria-label={props.label}
      aria-valuemin={props.percent === null ? undefined : 0}
      aria-valuemax={props.percent === null ? undefined : 100}
      aria-valuenow={props.percent ?? undefined}
      style={{
        height,
        borderRadius: height / 2,
        backgroundColor: 'var(--color-bg-tertiary)',
        overflow: 'hidden',
      }}
    >
      {props.percent === null ? (
        <div className="import-progress-indeterminate" style={{ height: '100%', width: '30%' }} />
      ) : (
        <div
          style={{
            height: '100%',
            width: `${props.percent.toString()}%`,
            backgroundColor: 'var(--color-accent-blue)',
            transition: 'width 0.4s ease',
          }}
        />
      )}
    </div>
  );
}

const PHASE_ICON = { done: '✓', active: '⟳', waiting: '—', unknown: '⟳' } as const;

function EntityLine(props: {
  readonly e: EntityProgress;
  readonly all: readonly EntityProgress[];
}): React.JSX.Element {
  const { e } = props;
  const phase = entityPhase(e);
  const text =
    phase === 'done'
      ? `${count(e.created, 'created', 'created')}, ${count(e.updated, 'updated', 'updated')}, ${count(e.skipped, 'skipped', 'skipped')}${(e.pendingLink ?? 0) > 0 ? `, ${count(e.pendingLink ?? 0, 'asked to join', 'asked to join')}` : ''}${e.failed > 0 ? `, ${count(e.failed, 'failed', 'failed')}` : ''}`
      : phase === 'active'
        ? `importing… (${(e.done ?? 0).toString()} of ${e.total.toString()})`
        : phase === 'waiting'
          ? `waiting — ${count(e.total, 'row')}`
          : `testing ${count(e.total, 'row')}…`;
  return (
    <li style={{ listStyle: 'none', padding: '8px 0' }}>
      <div style={{ display: 'flex', gap: 8, fontSize: 16, alignItems: 'baseline' }}>
        <span aria-hidden="true" style={{ width: 20, flexShrink: 0 }}>
          {PHASE_ICON[phase]}
        </span>
        <span style={{ fontWeight: 600 }}>{entityLabel(e, props.all)}:</span>
        <span style={{ color: phase === 'waiting' ? 'var(--color-text-secondary)' : undefined }}>
          {text}
        </span>
      </div>
      <div style={{ marginLeft: 28, marginTop: 6 }}>
        <Bar
          percent={percentDone(e.done, e.total)}
          label={`${entityLabel(e, props.all)} progress`}
          height={6}
        />
      </div>
    </li>
  );
}

function Progress(props: { readonly status: CommitStatus }): React.JSX.Element {
  const { status } = props;
  const { progress } = status;
  const queued = status.commitState === null || status.commitState === 'QUEUED';
  const heading = status.isDryRun
    ? 'Step 4 of 4 — Test run in progress…'
    : queued
      ? 'Step 4 of 4 — Starting the import…'
      : 'Step 4 of 4 — Importing…';
  const pct = percentDone(progress.done, progress.total);

  return (
    <Panel labelledBy="import-run-heading">
      <h2 id="import-run-heading" style={{ fontSize: '18px', fontWeight: 600 }}>
        {heading}
      </h2>
      <div style={{ marginTop: 16 }}>
        <Bar percent={queued ? null : pct} label="Import progress" />
      </div>
      {progress.done !== null && !queued && (
        <p style={{ ...muted, marginTop: 8 }}>
          {progress.done.toString()} of {count(progress.total, 'row')} done
        </p>
      )}
      <ul style={{ margin: '12px 0 0', padding: 0 }}>
        {progress.entities.map((e) => (
          <EntityLine
            key={`${e.entityFamily}|${e.sheetName ?? ''}`}
            e={e}
            all={progress.entities}
          />
        ))}
      </ul>
      {status.commitAttempts > 1 && (
        <p style={{ ...muted, marginTop: 8 }}>
          Picked up again after an interruption. Rows that were already written are not written
          twice.
        </p>
      )}
      <p style={{ fontSize: 16, marginTop: 16 }}>
        {status.isDryRun
          ? 'Nothing is saved by a test run.'
          : 'Records that others refer to are written first, then everything that depends on them. The import keeps running on the server if this page is closed.'}
      </p>
    </Panel>
  );
}

function UndoPanel(props: {
  readonly batchId: string;
  readonly created: number;
  readonly onUndone: (report: UndoReport) => void;
}): React.JSX.Element {
  const apiOpts = useApiOpts();
  const [confirming, setConfirming] = React.useState(false);
  const undo = useMutation<UndoReport, Error>({
    mutationFn: () => undoImport(apiOpts, props.batchId),
    onSuccess: (r) => {
      setConfirming(false);
      if (r.complete) props.onUndone(r);
    },
  });
  const report = undo.data;
  const lines = report === undefined ? null : blockerLines(report.blockers);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {report !== undefined && lines !== null && (
        <Notice tone="error" role="alert">
          <p style={{ fontWeight: 600 }}>
            Undo was refused — {count(report.refused, 'record is', 'records are')} still in use.
          </p>
          <ul style={{ margin: '8px 0 0 20px' }}>
            {lines.shown.map((m) => (
              <li key={m}>{m}</li>
            ))}
          </ul>
          {lines.hidden > 0 && <p style={{ marginTop: 8 }}>…and {lines.hidden.toString()} more.</p>}
          <p style={{ marginTop: 8 }}>
            {report.deleted > 0
              ? `${count(report.deleted, 'record', 'records')} that nothing referred to ${report.deleted === 1 ? 'was' : 'were'} removed.`
              : 'Nothing was removed.'}{' '}
            Clear those references, then press Undo again.
          </p>
        </Notice>
      )}
      {undo.isError && (
        <Notice tone="error" role="alert">
          {undo.error.message}
        </Notice>
      )}
      {confirming ? (
        <Notice tone="warn">
          <p>
            Undo removes the {count(props.created, 'record')} this import created. Records it
            updated keep their new values. If something else already refers to any record it
            created, nothing is removed, and the screen names what is in the way.
          </p>
          <div style={{ display: 'flex', gap: 12, marginTop: 12, flexWrap: 'wrap' }}>
            <ActionButton
              variant="primary"
              disabled={undo.isPending}
              onClick={(): void => undo.mutate()}
            >
              {undo.isPending ? 'Undoing…' : 'Yes, undo this import'}
            </ActionButton>
            <ActionButton disabled={undo.isPending} onClick={(): void => setConfirming(false)}>
              Cancel
            </ActionButton>
          </div>
        </Notice>
      ) : (
        <div>
          <ActionButton onClick={(): void => setConfirming(true)}>Undo this import</ActionButton>
        </div>
      )}
    </div>
  );
}

function Result(props: {
  readonly status: CommitStatus;
  readonly onRestart: () => void;
  readonly onImportForReal: (mode: CommitMode) => void;
  readonly onBackToPreview?: (() => void) | undefined;
}): React.JSX.Element {
  const apiOpts = useApiOpts();
  const { status } = props;
  // Held here, not in UndoPanel: a complete undo hides the panel (there is
  // nothing left to undo), and its counts must outlive it.
  const [undone, setUndone] = React.useState<UndoReport | null>(null);
  const summary = summaryOf(status);
  const [fileBusy, setFileBusy] = React.useState(false);
  const [fileError, setFileError] = React.useState<string | null>(null);

  const download = async (): Promise<void> => {
    setFileBusy(true);
    setFileError(null);
    try {
      // Signed on click, not per poll: the link expires, and the hauler may
      // come back to this screen later.
      const file = await getResultFile(apiOpts, status.batchId);
      window.open(file.downloadUrl, '_blank', 'noopener');
    } catch (err: unknown) {
      setFileError(err instanceof Error ? err.message : 'Could not get the import report.');
    } finally {
      setFileBusy(false);
    }
  };

  const heading = status.isDryRun
    ? '✓ Test run complete — nothing was saved'
    : undone !== null || status.status === 'UNDONE'
      ? 'Import undone'
      : summary.failed === 0
        ? '✓ Import complete'
        : '⚠ Import finished with some rows not imported';

  return (
    <Panel labelledBy="import-done-heading">
      <h2 id="import-done-heading" style={{ fontSize: '18px', fontWeight: 600 }}>
        {heading}
      </h2>
      {status.isDryRun && (
        <p style={{ fontSize: 16, marginTop: 8 }}>
          These are the results the import would have. Every row was checked against your data and
          then rolled back — nothing was created or changed.
        </p>
      )}
      {status.mode !== null && !status.isDryRun && (
        <p style={{ ...muted, marginTop: 4 }}>
          {status.mode === 'SKIP_EXISTING'
            ? 'Records you already had were left as they were.'
            : 'Records you already had were updated with the values in the file.'}
        </p>
      )}

      <table style={{ marginTop: 16, fontSize: 16, borderCollapse: 'collapse' }}>
        <tbody>
          {summary.families.map((f) => (
            <tr key={f.entityFamily}>
              <th scope="row" style={{ textAlign: 'left', padding: '6px 16px 6px 0' }}>
                {familyLabel(f.entityFamily)}:
              </th>
              <td style={{ padding: '6px 0' }}>
                {count(f.imported, 'created', 'created')}, {count(f.updated, 'updated', 'updated')},{' '}
                {count(f.skipped, 'skipped', 'skipped')}
                {f.matched > 0 ? `, ${count(f.matched, 'unchanged', 'unchanged')}` : ''}
                {(f.pendingLink ?? 0) > 0
                  ? `, ${count(f.pendingLink ?? 0, 'asked to join', 'asked to join')}`
                  : ''}
                {f.failed > 0 ? `, ${count(f.failed, 'failed', 'failed')}` : ''}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {(summary.pendingLink ?? 0) > 0 && (
        <p style={{ ...muted, marginTop: 8 }}>
          {count(summary.pendingLink ?? 0, 'person is', 'people are')} already on lodeX Pro at
          another company. {summary.pendingLink === 1 ? 'They were' : 'They were each'} asked to
          confirm in their app and join your team when they accept. Track them under Settings →
          Team.
        </p>
      )}

      {summary.alreadyDone > 0 && (
        <p style={{ ...muted, marginTop: 8 }}>
          {count(summary.alreadyDone, 'row was', 'rows were')} already imported by an earlier
          attempt and {summary.alreadyDone === 1 ? 'was' : 'were'} not written twice.
        </p>
      )}

      {summary.skippedReasons.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <h3 style={{ fontSize: 16, fontWeight: 600 }}>Rows not imported</h3>
          <ul style={{ margin: '8px 0 0 20px', fontSize: 16 }}>
            {summary.skippedReasons.map((r) => (
              <li key={r}>{r}</li>
            ))}
          </ul>
        </div>
      )}

      {!status.hasResultFile && !status.isDryRun && (
        <div style={{ marginTop: 16 }}>
          <Notice tone="warn">
            The rows were imported, but the row-by-row report file could not be created.
          </Notice>
        </div>
      )}
      {fileError !== null && (
        <div style={{ marginTop: 16 }}>
          <Notice tone="error" role="alert">
            {fileError}
          </Notice>
        </div>
      )}

      <div style={{ display: 'flex', gap: 12, marginTop: 20, flexWrap: 'wrap' }}>
        {status.hasResultFile && (
          <ActionButton
            variant="primary"
            disabled={fileBusy}
            onClick={(): void => {
              void download();
            }}
          >
            Download import report
          </ActionButton>
        )}
        {status.isDryRun && (
          <ActionButton
            variant="primary"
            onClick={(): void => props.onImportForReal(status.mode ?? 'UPDATE_ON_MATCH')}
          >
            Import for real
          </ActionButton>
        )}
        {status.isDryRun && props.onBackToPreview !== undefined && (
          <ActionButton onClick={props.onBackToPreview}>Back to the preview</ActionButton>
        )}
        <ActionButton onClick={props.onRestart}>Start another import</ActionButton>
      </div>

      {undone !== null && (
        <div style={{ marginTop: 16 }}>
          <Notice tone="ok" role="status">
            ✓ Import undone — {count(undone.deleted, 'record')} removed.
            {undone.skipped > 0 &&
              ` ${count(undone.skipped, 'record was', 'records were')} left in place, such as driver sign-in accounts.`}
          </Notice>
        </div>
      )}

      {undone === null && canUndo(status) && (
        <div
          style={{
            marginTop: 24,
            paddingTop: 16,
            borderTop: '1px solid var(--color-border-primary)',
          }}
        >
          <UndoPanel batchId={status.batchId} created={summary.imported} onUndone={setUndone} />
        </div>
      )}
    </Panel>
  );
}

export function RunStep(props: {
  readonly batchId: string;
  /** The mode to re-run with when the hauler presses "Try again" after a failure. */
  readonly mode: CommitMode;
  /** Fired once per poll result, so the wizard can arm/disarm its guard. */
  readonly onLiveChange: (live: boolean) => void;
  /** The run ended (DONE or FAILED) — the wizard stops remembering the batch. */
  readonly onSettled: () => void;
  /** Re-runs what failed: a test run is retried as a test run. */
  readonly onRetry: (mode: CommitMode, isDryRun: boolean) => void;
  /** From a finished test run: the same batch, committed with `isDryRun: false`. */
  readonly onImportForReal: (mode: CommitMode) => void;
  /** Absent when the wizard re-attached to a run it no longer has a preview for. */
  readonly onBackToPreview?: (() => void) | undefined;
  readonly onRestart: () => void;
}): React.JSX.Element {
  const apiOpts = useApiOpts();
  const { onLiveChange, onSettled } = props;

  const query = useQuery<CommitStatus, Error>({
    queryKey: ['imports', props.batchId, 'commit-status'],
    queryFn: () => getCommitStatus(apiOpts, props.batchId),
    refetchInterval: (q) => {
      const data = q.state.data;
      // A 404 will not get better by asking again.
      if (q.state.error instanceof ImportApiError && q.state.error.status === 404) return false;
      return data === undefined || isWaiting(data) ? POLL_MS : false;
    },
    refetchOnWindowFocus: false,
    // A blip is retried; a 404 (a stale remembered id, another workspace's
    // batch) is an answer, and retrying it only delays saying so.
    retry: (failures, err) =>
      !(err instanceof ImportApiError && err.status === 404) && failures < 2,
  });

  const status = query.data;
  const live = status === undefined || isWaiting(status);
  const notFound = query.error instanceof ImportApiError && query.error.status === 404;

  React.useEffect(() => {
    onLiveChange(live && !notFound);
  }, [live, notFound, onLiveChange]);

  const settled = status !== undefined && !isWaiting(status);
  React.useEffect(() => {
    if (settled || notFound) onSettled();
  }, [settled, notFound, onSettled]);

  if (notFound) {
    return (
      <Panel>
        <Notice tone="error" role="alert">
          This import could not be found. It may belong to a different workspace.
        </Notice>
        <div style={{ marginTop: 16 }}>
          <ActionButton onClick={props.onRestart}>Start a new import</ActionButton>
        </div>
      </Panel>
    );
  }

  if (status === undefined) {
    if (query.isError) {
      return (
        <Panel>
          <Notice tone="error" role="alert">
            Could not check on the import: {query.error.message} It is still running on the server.
          </Notice>
          <div style={{ marginTop: 16 }}>
            <ActionButton onClick={(): void => void query.refetch()}>Check again</ActionButton>
          </div>
        </Panel>
      );
    }
    return <Panel>Starting the import…</Panel>;
  }

  if (isWaiting(status)) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {query.isError && (
          <Notice tone="warn" role="status">
            Lost contact while checking progress — still trying. The import keeps running on the
            server.
          </Notice>
        )}
        <Progress status={status} />
      </div>
    );
  }

  if (status.commitState === 'FAILED') {
    return (
      <Panel labelledBy="import-failed-heading">
        <h2 id="import-failed-heading" style={{ fontSize: '18px', fontWeight: 600 }}>
          ✗ The import stopped before it finished
        </h2>
        <div style={{ marginTop: 12 }}>
          <Notice tone="error" role="alert">
            {status.error ?? 'The import could not finish.'}
          </Notice>
        </div>
        <p style={{ fontSize: 16, marginTop: 12 }}>
          Rows that were already written are kept. Running the import again picks up where it
          stopped and does not write any row twice.
        </p>
        <div style={{ display: 'flex', gap: 12, marginTop: 16, flexWrap: 'wrap' }}>
          <ActionButton
            variant="primary"
            onClick={(): void => props.onRetry(status.mode ?? props.mode, status.isDryRun)}
          >
            {status.isDryRun ? 'Try the test run again' : 'Try the import again'}
          </ActionButton>
          {props.onBackToPreview !== undefined && (
            <ActionButton onClick={props.onBackToPreview}>Back to the preview</ActionButton>
          )}
          <ActionButton variant="ghost" onClick={props.onRestart}>
            Start another import
          </ActionButton>
        </div>
      </Panel>
    );
  }

  return (
    <Result
      status={status}
      onRestart={props.onRestart}
      onImportForReal={props.onImportForReal}
      onBackToPreview={props.onBackToPreview}
    />
  );
}
